/**
 * SocialPage Component
 * Optimized main page for social feed and post creation
 */

import React, { useState, useEffect } from 'react';
import { 
  Image as ImageIcon, 
  Video, 
  MapPin, 
  X, 
  Loader, 
  ArrowLeft,
  RefreshCw,
  MessageSquare,
  User,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

// Import auth context and services
import { useAuth } from '../../../core/contexts/AuthContext';
import socialService from '../services/socialService';

// Import SocialFeed component
import SocialFeed from '../components/SocialFeed';

/**
 * SocialPage Component - Optimized version
 */
const SocialPage = () => {
  // Auth context for user info
  const { currentUser, userProfile } = useAuth();
  
  // Post creation state
  const [postContent, setPostContent] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [location, setLocation] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  
  // Feed state
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(true);

  // Fetch initial posts
  const fetchInitialPosts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { posts, lastVisible, hasMore } = await socialService.getFeedPosts(5);
      setPosts(posts);
      setLastVisible(lastVisible);
      setHasMore(hasMore);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load posts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch more posts for infinite scrolling
  const fetchMorePosts = async () => {
    if (!hasMore || loading || !lastVisible) return;
    
    setLoading(true);
    
    try {
      const { posts: newPosts, lastVisible: newLastVisible, hasMore: newHasMore } = 
        await socialService.getFeedPosts(5, lastVisible);
      
      setPosts(prevPosts => [...prevPosts, ...newPosts]);
      setLastVisible(newLastVisible);
      setHasMore(newHasMore);
    } catch (err) {
      console.error('Error fetching more posts:', err);
      setError('Failed to load more posts. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Load initial posts on component mount
  useEffect(() => {
    fetchInitialPosts();
  }, []);
  
  // Handle refresh button click
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchInitialPosts();
    setIsRefreshing(false);
  };

  // Handle media file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 20MB for better mobile experience)
    if (file.size > 20 * 1024 * 1024) {
      setError('File size exceeds 20MB. Please choose a smaller file.');
      return;
    }

    // Create a preview URL
    const previewUrl = URL.createObjectURL(file);
    setMediaPreview(previewUrl);
    setMediaFile(file);

    // Determine media type
    if (file.type.startsWith('image/')) {
      setMediaType('image');
    } else if (file.type.startsWith('video/')) {
      setMediaType('video');
    } else {
      setError('Unsupported file type. Please upload an image or video.');
      setMediaFile(null);
      setMediaPreview(null);
    }
  };

  // Clear selected media
  const clearMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
    setUploadProgress(0);
    
    // Reset the file input
    const fileInput = document.getElementById('media-upload');
    if (fileInput) fileInput.value = '';
  };

  // Get user's location
  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          toast.success('Location added');
        },
        (error) => {
          console.error('Error getting location:', error);
          setError('Unable to get your location. Please try again.');
        }
      );
    } else {
      setError('Geolocation is not supported by your browser.');
    }
  };

  // Clear location
  const clearLocation = () => {
    setLocation(null);
  };

  // Create a new post
  const createPost = async () => {
    if (!postContent.trim() && !mediaFile) {
      setError('Please add some text or media to your post.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Prepare post data
      const postData = {
        author: {
          avatar: userProfile?.photos?.[0] || '',
          name: userProfile?.basicInfo?.name || 'Anonymous',
          username: userProfile?.basicInfo?.username || '',
        },
        authorId: currentUser.uid,
        content: postContent.trim(),
        study_tags: userProfile?.academic ? [
          {
            university: userProfile.academic.university || null,
            degree: userProfile.academic.degree || null
          }
        ] : []
      };

      // Add location if available
      if (location) {
        postData.location = location;
      }

      // Create the post using socialService
      await socialService.createPost(
        postData,
        mediaFile,
        mediaType,
        (progress) => setUploadProgress(progress)
      );

      // Reset form
      setPostContent('');
      clearMedia();
      clearLocation();
      
      // Refresh feed to show new post
      fetchInitialPosts();
    } catch (err) {
      console.error('Error creating post:', err);
      setError('Failed to create post. Please try again.');
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  // Toggle create post visibility
  const toggleCreatePost = () => {
    setShowCreatePost(!showCreatePost);
  };

  return (
    <div className="container mx-auto max-w-2xl pb-20">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 px-2">
        <Link to="/home" className="text-gray-600 hover:text-gray-800">
          <ArrowLeft size={20} />
        </Link>
        {/* <h1 className="text-lg font-bold text-center flex-1">Social Feed</h1> */}
        <button 
          className={`text-gray-600 hover:text-gray-800 p-1 ${isRefreshing ? 'animate-spin' : ''}`}
          onClick={handleRefresh}
          disabled={isRefreshing || loading}
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Create Post Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 overflow-hidden">
        {/* Create Post Header - Always visible */}
        <div className="p-3 border-b border-gray-100 flex justify-between items-center">
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden flex-shrink-0 mr-2">
              {userProfile?.photos && userProfile.photos.length > 0 ? (
                <img 
                  src={userProfile.photos[0]} 
                  alt="Your avatar" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-green-100 text-green-500">
                  {userProfile?.basicInfo?.name?.charAt(0) || <User size={18} />}
                </div>
              )}
            </div>
            <div className="text-sm font-medium text-gray-800">
              {!showCreatePost ? 'Share something with your peers...' : ''}
            </div>
          </div>
          
          <div>
            <button 
              onClick={toggleCreatePost} 
              className="text-sm text-green-500 font-medium flex items-center"
            >
              {showCreatePost ? 'Collapse' : 'Create Post'} 
              <ChevronRight size={16} className={`ml-1 transform transition-transform ${showCreatePost ? 'rotate-90' : ''}`} />
            </button>
          </div>
        </div>
        
        {/* Expandable Create Post Body */}
        {showCreatePost && (
          <div className="p-3">
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none min-h-[80px]"
              placeholder="What's on your mind?"
              value={postContent}
              onChange={(e) => setPostContent(e.target.value)}
              disabled={isSubmitting}
            />
            
            {/* Media Preview */}
            {mediaPreview && (
              <div className="mt-2 relative bg-gray-100 rounded-lg overflow-hidden">
                <button 
                  className="absolute right-2 top-2 bg-black bg-opacity-60 rounded-full p-1 text-white z-10"
                  onClick={clearMedia}
                >
                  <X size={16} />
                </button>
                {mediaType === 'image' ? (
                  <div className="max-h-48 overflow-hidden">
                    <img 
                      src={mediaPreview} 
                      alt="Upload preview" 
                      className="w-auto mx-auto max-h-48 object-contain"
                    />
                  </div>
                ) : (
                  <div className="max-h-48 overflow-hidden">
                    <video 
                      src={mediaPreview} 
                      controls 
                      className="max-h-48 w-auto mx-auto"
                    />
                  </div>
                )}
                
                {/* Upload Progress */}
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                    <div className="bg-white rounded-lg p-2 text-center">
                      <div className="w-8 h-8 mx-auto mb-1 relative">
                        <div className="absolute inset-0 border-2 border-t-transparent border-green-500 rounded-full animate-spin"></div>
                      </div>
                      <div className="text-xs font-medium">{uploadProgress}%</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Location Tag */}
            {location && (
              <div className="mt-2 bg-blue-50 text-blue-700 py-1 px-3 rounded-full text-xs inline-flex items-center">
                <MapPin size={12} className="mr-1" />
                Location added
                <button className="ml-1 text-blue-700" onClick={clearLocation}>
                  <X size={12} />
                </button>
              </div>
            )}

            {/* Error Message */}
            {error && !error.includes('posts') && (
              <div className="mt-2 text-red-500 text-xs p-2 bg-red-50 rounded">
                {error}
              </div>
            )}

            {/* Post Actions */}
            <div className="mt-3 flex justify-between items-center">
              <div className="flex space-x-2">
                <label 
                  htmlFor="media-upload" 
                  className="p-2 text-gray-500 hover:text-green-500 rounded-full cursor-pointer"
                  title="Add image or video"
                >
                  <ImageIcon size={18} />
                  <input 
                    id="media-upload" 
                    type="file" 
                    accept="image/*,video/*" 
                    className="hidden" 
                    onChange={handleFileChange}
                    disabled={isSubmitting}
                  />
                </label>
                <button 
                  className="p-2 text-gray-500 hover:text-green-500 rounded-full"
                  onClick={getLocation}
                  disabled={isSubmitting || location !== null}
                  title="Add your location"
                >
                  <MapPin size={18} />
                </button>
              </div>
              <button 
                className="px-4 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-full text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={createPost}
                disabled={isSubmitting || (!postContent.trim() && !mediaFile)}
              >
                {isSubmitting ? 'Posting...' : 'Post'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Feed Error Message */}
      {error && error.includes('posts') && (
        <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
          {error}
          <button 
            className="ml-2 underline text-red-700"
            onClick={fetchInitialPosts}
          >
            Retry
          </button>
        </div>
      )}

      {/* Social Feed */}
      <SocialFeed 
        posts={posts}
        loading={loading}
        error={error && error.includes('posts') ? error : null}
        hasMore={hasMore}
        fetchMorePosts={fetchMorePosts}
        formatTimestamp={socialService.formatTimestamp}
        onRefresh={handleRefresh}
      />

      {/* Empty State */}
      {posts.length === 0 && !loading && !error && (
        <div className="text-center py-8 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="text-gray-400 mb-3">
            <MessageSquare size={40} className="mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">No posts yet</h3>
          <p className="text-gray-500 mb-4 px-6">Be the first to share something with your peers!</p>
          <button
            onClick={toggleCreatePost}
            className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600"
          >
            Create Your First Post
          </button>
        </div>
      )}
    </div>
  );
};

export default SocialPage;